/**
 *
 * DESCRIPTION:
 * This script detects <react-adslot /> elements in a HTML page and initializes react components in those slots.
 * The react component that is created can be configured using data attributes, see under function declaration below.
 *
 *    data-g-ad-position - {string} Tradera domain specific custom targeting parameter e.g. mid1-desktop, see google ad manager (DFP) for more info
 *    data-g-ad-format - {string} comma separated, maps to an ad size, see ad-slot-utilities.js
 *    data-g-ad-device - {string} maps to viewport according to our breakpoints, see ad-slot-utilities.js
 *    data-g-ad-lazy-load - {bool} enable lazy load
 *    data-g-ad-only-on-pages - {string} comma separated, specify pages where ad should be visible, OR filter, uses our pagename in init-data
 *    data-g-ad-sticky - {bool} enable sticky
 *    data-g-ad-autoscale  - {bool} enable autoscale, which resizes an ad to fit the available space to parent
 *    data-g-ad-sticky-top - {string (#id) or number} if sticky is enabled, specifies top boundary
 *    data-g-ad-sticky-bottom - {string (#id) or number} if sticky is enabled, specifies top boundary
 *    data-g-ad-ad-container-class - {string} possibility to wrap entire ad component in div with class specified as value
 */

import React from 'react';
import ReactDOM from 'react-dom';
import logger from 'packages/logger';
import {
    createGptSizeMapping,
    mapDeviceNameToViewport
} from 'static/script/app/ui/layout/ads/ad-slot-utilities';
import DfpAdInitDataDecorator from './dfp-ad-init-data-decorator';
import Messaging from 'packages/react/messaging';
import initTakeovers from './boot-takeover-ads';

/**
 * initializes the react into the DOM element
 * @param {*HTMLDomElement} element a HTML Dom element for the <react-adslot> that is to be initialized.
 * @param {*} initData The init data element that is part of every page request
 */
const initReactElement = (element, initData) => {
    // get all element attributes
    const position = element.getAttribute('data-g-ad-position');
    const adFormats = element.getAttribute('data-g-ad-format');
    const deviceName = element.getAttribute('data-g-ad-device');
    const lazyLoad = element.hasAttribute('data-g-ad-lazy-load');
    const adOnlyOnPages = element.getAttribute('data-g-ad-only-on-pages');
    const stickyEnabled = element.hasAttribute('data-g-ad-sticky');
    const autoscale = element.hasAttribute('data-g-ad-autoscale');
    let stickyTop = element.getAttribute('data-g-ad-sticky-top');
    let stickyBottom = element.getAttribute('data-g-ad-sticky-bottom');
    let adContainerClass = element.getAttribute('data-g-ad-ad-container-class');

    const refresh =
        element.hasAttribute('data-g-ad-refresh') &&
        'ads-refresh' in initData.featureSwitches &&
        initData.featureSwitches['ads-refresh'] === true;

    // check if this adslot has specific exclusion rules
    if (adOnlyOnPages !== null) {
        // if page not found in list of ads to load then exit
        if (
            adOnlyOnPages
                .split(',')
                .indexOf(initData.gptData.customTargetingParameters.page) === -1
        ) {
            return;
        }
    }

    // handle sticky properties
    if (stickyTop !== null && !isNaN(stickyTop)) {
        stickyTop = parseInt(stickyTop);
        if (!initData.isLoggedIn) {
            stickyTop += 38;
        }
    }
    if (stickyBottom !== null && !isNaN(stickyBottom)) {
        stickyBottom = parseInt(stickyBottom);
    }

    // get size mapping
    const sizeMapping = createGptSizeMapping(adFormats, deviceName);
    const [minViewportWidth, maxViewportWidth] = mapDeviceNameToViewport(
        deviceName
    );

    if (element && sizeMapping) {
        try {
            ReactDOM.render(
                <DfpAdInitDataDecorator
                    minViewportWidth={minViewportWidth}
                    maxViewportWidth={maxViewportWidth}
                    sizeMapping={sizeMapping}
                    slotTargeting={{ pos: position }}
                    initData={initData}
                    lazyLoad={lazyLoad}
                    stickyEnabled={stickyEnabled}
                    stickyTop={stickyTop}
                    stickyBottom={stickyBottom}
                    refresh={refresh}
                    autoscale={autoscale}
                    autoscaleOffsetWidth={
                        autoscale === true ? element.offsetWidth : undefined
                    }
                    adContainerClass={adContainerClass}
                />,
                element
            );

            // make sure that we kill our react stuff once a pjax happens
            const messages = new Messaging();
            messages.subscribe(['data:navigation:after-page-load'], function() {
                ReactDOM.unmountComponentAtNode(element);
                messages.teardown();
            });
        } catch (e) {
            logger(e);
        }
    }
};

/**
 * Finds all react ad elements in the DOM and initializes them if they have not previously been initialized
 * @param {*object} initData
 */
const loadReactElements = initData => {
    // find all the <react-adslot> elements on the page
    Array.from(document.getElementsByTagName('react-adslot')).forEach(
        element => {
            if (!element.hasAttribute('data-react-loaded')) {
                initReactElement(element, initData);
                element.setAttribute('data-react-loaded', true);
            }
        }
    );
};

/**
 * This is the main loader function to init the ads on a page. This function should only be called once per "loaded page."
 * @param {*object} initData
 */
const reactAdsLoader = initData => {
    if (
        'ads-leeads-takeover' in initData.featureSwitches &&
        initData.featureSwitches['ads-leeads-takeover'] === true
    ) {
        initTakeovers();
    }
    loadReactElements(initData);

    // make sure that on mobile pagination that the new <react-adslot> that are sent are loaded
    const messages = new Messaging();
    messages.subscribe(
        [
            'ui:search-result:after-pagination',
            'data:navigation-filter:after-load'
        ],
        function() {
            loadReactElements(initData);
        }
    );
    messages.subscribe(['data:navigation:after-page-load'], function() {
        messages.teardown();
    });
};

export default reactAdsLoader;



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/components/ads/boot-react-ads.js